import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag,
  Drawer, Form, Input, message, Row, Col, DatePicker, Switch,
} from 'antd'
import { VideoCameraOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const OBSERVATION_TYPES = ['SUSPICIOUS', 'CAMERA_DOWN', 'MOTION', 'TRESPASS', 'THEFT', 'OTHER']

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'processing',
  REVIEWED: 'warning',
  CLOSED: 'success',
}

export default function CCTVLogPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [obsTypeFilter, setObsTypeFilter] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: logs, isLoading } = useQuery({
    queryKey: ['cctv-logs', plantId, obsTypeFilter, statusFilter],
    queryFn: () =>
      apiClient.get(`${API}/cctv-logs`, {
        params: {
          plant_id: plantId,
          status: statusFilter || undefined,
          observation_type: obsTypeFilter || undefined,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/cctv-logs`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cctv-logs'] })
      message.success('CCTV event logged')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`${API}/cctv-logs/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cctv-logs'] })
      message.success('Updated')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const list = Array.isArray(logs) ? logs : []

  const columns = [
    {
      title: 'Event No',
      dataIndex: 'cctv_event_no',
      key: 'cctv_event_no',
      width: 120,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
    },
    {
      title: 'Event Time',
      dataIndex: 'event_time',
      key: 'event_time',
      width: 140,
      render: (v: string) => v ? dayjs(v).format('DD-MMM HH:mm') : '—',
    },
    { title: 'Camera ID', dataIndex: 'camera_id', key: 'camera_id', width: 110 },
    { title: 'Location', dataIndex: 'camera_location', key: 'camera_location', width: 140 },
    {
      title: 'Observation',
      dataIndex: 'observation_type',
      key: 'observation_type',
      width: 130,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Clip Saved',
      dataIndex: 'clip_saved',
      key: 'clip_saved',
      width: 100,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'YES' : 'NO'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: unknown, r: any) => (
        <Space>
          {r.status === 'OPEN' && (
            <Button
              size="small"
              onClick={() => updateMut.mutate({ id: r.id, data: { status: 'REVIEWED' } })}
              loading={updateMut.isPending}
            >
              Mark Reviewed
            </Button>
          )}
          {(r.status === 'OPEN' || r.status === 'REVIEWED') && (
            <Button
              size="small"
              type="primary"
              onClick={() => updateMut.mutate({ id: r.id, data: { status: 'CLOSED' } })}
              loading={updateMut.isPending}
            >
              Close
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <VideoCameraOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        CCTV Exception Log
      </Title>
      <Typography.Text type="secondary">Log and manage CCTV observations and exceptions</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} justify="space-between">
          <Col>
            <Space wrap>
              <Select
                value={plantId}
                onChange={setPlantId}
                style={{ width: 200 }}
                options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
              />
              <Select
                allowClear
                placeholder="Observation Type"
                style={{ width: 160 }}
                value={obsTypeFilter}
                onChange={v => setObsTypeFilter(v)}
                options={OBSERVATION_TYPES.map(t => ({ value: t, label: t }))}
              />
              <Select
                allowClear
                placeholder="Status"
                style={{ width: 130 }}
                value={statusFilter}
                onChange={v => setStatusFilter(v)}
                options={['OPEN', 'REVIEWED', 'CLOSED'].map(s => ({ value: s, label: s }))}
              />
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { form.resetFields(); setDrawerOpen(true) }}
            >
              Log Event
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 1100 }}
        />
      </Card>

      <Drawer
        title="Log CCTV Event"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields() }}
        width={480}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending}
                onClick={() =>
                  form.validateFields().then(vals => {
                    const payload = {
                      ...vals,
                      plant_id: plantId,
                      event_time: vals.event_time
                        ? vals.event_time.toISOString()
                        : undefined,
                    }
                    createMut.mutate(payload)
                  })
                }
              >
                Submit
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical" initialValues={{ clip_saved: false }}>
          <Form.Item name="camera_id" label="Camera ID" rules={[{ required: true }]}>
            <Input placeholder="e.g. CAM-001" />
          </Form.Item>
          <Form.Item name="camera_location" label="Camera Location" rules={[{ required: true }]}>
            <Input placeholder="e.g. Main Gate" />
          </Form.Item>
          <Form.Item name="event_time" label="Event Time" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="observation_type" label="Observation Type" rules={[{ required: true }]}>
            <Select options={OBSERVATION_TYPES.map(t => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="clip_saved" label="Clip Saved?" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="action_taken" label="Action Taken">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
